import React, { useEffect, useState } from "react";
import { apiClient } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Truck, MapPin, Loader2, ArrowRight, ShieldCheck } from "lucide-react";

export default function DeliveryPage() {
  const { lang } = useI18n();
  const [providers, setProviders] = useState([]);
  const [form, setForm] = useState({ pickup_city: "Riyadh", dropoff_city: "Jeddah", distance_km: 950, insurance: true });
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => { apiClient.get("/delivery/providers").then(({ data }) => setProviders(data.items || [])); }, []);

  const getQuote = async (p) => {
    setSelected(p); setBusy(true); setQuote(null);
    try {
      const { data } = await apiClient.post("/delivery/quote", { ...form, provider_id: p.id });
      setQuote(data);
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-12" data-testid="delivery-page">
      <div className="overline mb-2">DELIVERY MARKETPLACE</div>
      <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-medium">{lang === "ar" ? "وصّل سيارتك بأمان" : "Ship your vehicle, safely"}</h1>
      <p className="text-[#6b6b6b] mt-2 max-w-2xl">{lang === "ar" ? "أسعار فورية، تتبع مباشر، وتأمين شامل لكل رحلة." : "Instant quotes, live tracking, and full-cover insurance on every delivery."}</p>

      <div className="grid lg:grid-cols-[1fr_1.4fr] gap-8 mt-10">
        <div className="surface rounded-3xl p-8 lg:sticky lg:top-24 lg:self-start space-y-4">
          <div className="overline">ROUTE</div>
          <div>
            <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "من" : "Pickup city"}</label>
            <div className="relative mt-1">
              <MapPin className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#c9a55c]" />
              <input className="input-dark ps-10" value={form.pickup_city} onChange={(e) => setForm({...form, pickup_city: e.target.value})} data-testid="delivery-pickup" />
            </div>
          </div>
          <div>
            <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "إلى" : "Dropoff city"}</label>
            <div className="relative mt-1">
              <MapPin className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#c9a55c]" />
              <input className="input-dark ps-10" value={form.dropoff_city} onChange={(e) => setForm({...form, dropoff_city: e.target.value})} data-testid="delivery-dropoff" />
            </div>
          </div>
          <div>
            <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "المسافة (كم)" : "Distance (km)"}</label>
            <input type="number" className="input-dark mt-1" value={form.distance_km} onChange={(e) => setForm({...form, distance_km: Number(e.target.value)})} data-testid="delivery-distance" />
          </div>
          <label className="flex items-center gap-2 text-sm text-[#525252]">
            <input type="checkbox" checked={form.insurance} onChange={(e) => setForm({...form, insurance: e.target.checked})} className="accent-cyan-400" data-testid="delivery-insurance" />
            <ShieldCheck className="w-4 h-4 text-[#8a6f2e]" />
            {lang === "ar" ? "تأمين كامل (+150 SAR)" : "Full insurance (+150 SAR)"}
          </label>
          {quote && (
            <div className="mt-4 p-4 rounded-xl bg-cyan-400/5 border border-[#c9a55c]/40" data-testid="delivery-quote">
              <div className="overline">QUOTE</div>
              <div className="font-display text-3xl text-[#8a6f2e] mt-2">{quote.total_sar.toLocaleString()} <span className="text-sm text-[#6b6b6b]">SAR</span></div>
              <div className="text-xs text-[#6b6b6b] mt-1">ETA: {quote.eta}</div>
              <div className="text-xs text-[#6b6b6b]">via {quote.provider.provider_en}</div>
            </div>
          )}
        </div>

        <div className="space-y-4">
          {providers.map((p) => (
            <div key={p.id} className={`glass rounded-2xl p-6 flex items-center gap-4  ${selected?.id === p.id ? "border-cyan-400/60" : ""}`} data-testid={`delivery-provider-${p.id}`}>
              <div className="w-12 h-12 rounded-xl bg-[#c9a55c]/15 border border-[#c9a55c]/40 flex items-center justify-center"><Truck className="w-5 h-5 text-[#8a6f2e]" /></div>
              <div className="flex-1">
                <div className="font-display text-lg">{lang === "ar" ? p.provider_ar : p.provider_en}</div>
                <div className="text-xs text-[#6b6b6b]">{p.delivery_time} · {p.insurance_option}</div>
                <div className="text-xs text-[#6b6b6b] mt-1">Base {p.base_price_sar} SAR · {p.per_km_sar} SAR/km</div>
              </div>
              <button className="btn-primary text-sm" onClick={() => getQuote(p)} disabled={busy}>
                {busy && selected?.id === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                {lang === "ar" ? "احصل على عرض" : "Get quote"}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
